import _ from 'lodash';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { createItem, pruneDehydratedItemProps, shouldDehydrateItem } from '@/trendData/itemStore.utilities';
import {
  DASH_STYLES,
  ITEM_DATA_STATUS,
  ITEM_TYPES,
  SAMPLE_OPTIONS,
  Y_AXIS_TYPES,
} from '@/trendData/trendData.constants';
import type { Item } from '@/trendData/baseItem.store';
import type { DehydratedMetricItem, MetricItem } from '@/trendData/trendData.types';

interface MetricPayload {
  id: string;
  name: string;
  lane: number;
  alignment: string;
  color?: string;
}

export type MetricDehydratedState = Pick<BaseItemStore<Item>, 'items'> & {
  items: DehydratedMetricItem[];
};

export class TrendMetricStore extends BaseItemStore {
  static readonly storeName = 'sqTrendMetricStore';

  dehydrate(): MetricDehydratedState {
    return {
      items: _.chain(this.state.get('items'))
        .filter(shouldDehydrateItem)
        .map((item) => pruneDehydratedItemProps(item) as DehydratedMetricItem)
        .value(),
    };
  }

  rehydrate(dehydratedState: MetricDehydratedState): void {
    this.state.merge(_.omit(dehydratedState, ['items']));
    this.state.set(
      'items',
      _.map(dehydratedState.items, (item) =>
        this.createMetric(
          item.id,
          item.name,
          item.lane,
          item.axisAlign,
          _.omit(item, ['id', 'name']) as Partial<MetricItem>,
        ),
      ),
    );
  }

  protected readonly handlers = {
    ...super.baseHandlers,
    TREND_ADD_METRIC: (payload: MetricPayload) => {
      this.state.push(
        'items',
        this.createMetric(payload.id, payload.name, payload.lane, payload.alignment, _.pick(payload, ['color'])),
      );
    },
  } as const;

  /**
   * Adds a Metric item to the specified panel.
   *
   * @param payload - Object container
   * @param payload.id- The id of the metric
   * @param payload.name - The name of the metric
   * @param payload.lane - Lane of the new item
   * @param payload.alignment - Alignment of the new item
   * @param [payload.color] - optional color.
   */
  addMetric(payload: MetricPayload): void {
    this.state.push(
      'items',
      this.createMetric(payload.id, payload.name, payload.lane, payload.alignment, _.pick(payload, ['color'])),
    );
  }

  /**
   * Creates a metric item.
   *
   * @param id - The id of the metric
   * @param name - The name of the metric
   * @param lane - Lane to use for the new series
   * @param alignment - Alignment to use for the new series
   * @param props[] - Array of properties to be set on the item.
   * @return {Object} an table item.
   */
  createMetric(id: string, name: string, lane: number, alignment: string, props: Partial<MetricItem>): MetricItem {
    return createItem(
      id,
      name,
      ITEM_TYPES.METRIC,
      _.assign(
        {
          calculationType: TREND_TOOLS.THRESHOLD_METRIC,
          yAxisConfig: {},
          dashStyle: _.get(props, 'dashStyle', DASH_STYLES.SOLID),
          lane,
          axisAlign: alignment,
          axisVisibility: _.get(props, 'axisVisibility', true),
          axisAutoScale: _.get(props, 'axisAutoScale', true),
          lineWidth: _.get(props, 'lineWidth', 1),
          sampleDisplayOption: props.sampleDisplayOption || SAMPLE_OPTIONS.LINE,
          dataStatus: ITEM_DATA_STATUS.INITIALIZING,
          yAxisType: Y_AXIS_TYPES.LINEAR,
          data: [],
          statusMessage: '',
        },
        props,
      ),
    ) as MetricItem;
  }
}
